import { useEffect, useState } from "react";

/**
 * Responsive breakpoints (max-width, px). Mirrors the SCSS source of truth in
 * `assets/scss/abstracts/variables/_breakpoints.scss` so JS-side viewport logic
 * (e.g. auto-collapsing the nav drawer, reflowing the dashboard grid) stays in
 * lock-step with the stylesheet media queries.
 */
export const BREAKPOINTS = {
    small: 480,
    mobile: 850,
    tablet: 1030,
    desktop: 1300,
    desktop_large: 1600,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Reactively track whether a CSS media query currently matches the viewport.
 *
 * Returns `false` in non-browser contexts (SSR / jsdom without matchMedia) so it
 * is safe to call unconditionally. Subscribes to viewport changes and cleans up
 * on unmount.
 */
export const useMediaQuery = (query: string): boolean => {
    const getMatch = () =>
        typeof window !== "undefined" && typeof window.matchMedia === "function"
            ? window.matchMedia(query).matches
            : false;

    const [matches, setMatches] = useState<boolean>(getMatch);

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return undefined;
        }
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        // Sync immediately in case the viewport changed between render and effect.
        onChange();
        // Safari < 14 only exposes the deprecated addListener/removeListener API.
        if (typeof mql.addEventListener === "function") {
            mql.addEventListener("change", onChange);
            return () => mql.removeEventListener("change", onChange);
        }
        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [query]);

    return matches;
};

/**
 * True when the viewport is at or below the given breakpoint (default: `mobile`,
 * 850px) — the canonical "is this a narrow / mobile viewport" check.
 */
export const useIsNarrow = (maxWidth: number = BREAKPOINTS.mobile): boolean =>
    useMediaQuery(`(max-width: ${maxWidth}px)`);
